import { defineStore, } from 'pinia'

import axios from "@axios"
import localforage from "localforage"
import {reportError} from "@/plugins/misc"

const baseUrl = `/usergroup/`

export const useUserGroupsStore = defineStore({
  id: 'userGroups',
  state: () => ({
    userGroups: [],
    restaurantUserGroups: [],
    adminEditingUserGroup: null,
    adminEditingUserGroupMembers: null,
    adminEditingUserGroupSubscription: null,
    guestUserGroup: null,
    isInitialized: false,
    fetching: false,
    saving: false
  }),
  actions: {
    async loadInitialData() {
      if (this.isInitialized) {
        return  // If already initialized, do nothing
      }

      const storedUserGroups = await localforage.getItem('userGroups')
      if (storedUserGroups) {
        this.userGroups = JSON.parse(storedUserGroups)
      }
      const storedRestaurantUserGroups = await localforage.getItem('restaurantUserGroups')
      if (storedRestaurantUserGroups) {
        this.restaurantUserGroups = JSON.parse(storedRestaurantUserGroups)
      }

      this.isInitialized = true
    },

    async getAllUserGroups(force = false) {
      // OPTIMIZATION: Cache user groups for 5 minutes to avoid repeated API calls
      const now = Date.now()
      const cacheKey = 'userGroupsCache'
      const cacheTimeKey = 'userGroupsCacheTime'
      const cacheTTL = 5 * 60 * 1000 // 5 minutes

      // Check if we have cached data and it's not stale
      if (!force && this.userGroups && this.userGroups.length > 0) {
        const cacheTime = localStorage.getItem(cacheTimeKey)
        if (cacheTime && (now - parseInt(cacheTime)) < cacheTTL) {
          return // Use existing data
        }
      }

      this.fetching = true
      try {
        // JWT now sent automatically via cookies (no need in params)
        let res = await axios.get(`${baseUrl}`)
        this.userGroups = res.data
        this.restaurantUserGroups = this.userGroups.filter(userGroup => userGroup.type == 'restaurant')
                
        // Cache in localforage and localStorage
        await localforage.setItem('restaurantUserGroups', JSON.stringify(this.restaurantUserGroups))
        await localforage.setItem('userGroups', JSON.stringify(this.userGroups))
        localStorage.setItem(cacheTimeKey, now.toString())
                
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:getAllUserGroups", error.message, null,null,null, error)
        this.fetching = false
      }
    },

    async getMyUserGroups(force = false) {
      // OPTIMIZATION: Cache my user groups for 5 minutes
      const now = Date.now()
      const cacheTimeKey = 'myUserGroupsCacheTime'
      const cacheTTL = 5 * 60 * 1000 // 5 minutes

      // Check cache
      if (!force && this.userGroups && this.userGroups.length > 0) {
        const cacheTime = localStorage.getItem(cacheTimeKey)
        if (cacheTime && (now - parseInt(cacheTime)) < cacheTTL) {
          // Ensure restaurantUserGroups is also set from cached data
          if (!this.restaurantUserGroups || this.restaurantUserGroups.length === 0) {
            this.restaurantUserGroups = this.userGroups.filter(userGroup => userGroup.type == 'restaurant')
          }

          return
        }
      }

      this.fetching = true
      try {
        // JWT now sent automatically via cookies
        let res = await axios.get(`${baseUrl}`, {
          params:{ mine: true }
        })
        this.userGroups = res.data
        this.restaurantUserGroups = this.userGroups.filter(userGroup => userGroup.type == 'restaurant')
        await localforage.setItem('restaurantUserGroups', JSON.stringify(this.restaurantUserGroups))
        await localforage.setItem('userGroups', JSON.stringify(this.userGroups))
        localStorage.setItem(cacheTimeKey, now.toString())
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:getAllUserGroups", error.message, null,null,null, error)
        this.fetching = false
      }
    },

    async getSearchedUserGroups(searchTerm) {
      this.fetching = true
      try {
        // JWT now sent automatically via cookies
        let res = await axios.get(baseUrl, {
          params: { searchTerm: searchTerm }
        })

        // Merge the new data with the existing data
        this.userGroups = [...this.userGroups, ...res.data]

        // Remove duplicates if necessary (based on some unique property, e.g., 'id')
        this.userGroups = [...new Map(this.userGroups.map(item => [item._id, item])).values()]

        //this might not be needed and/or inefficient
        this.restaurantUserGroups = this.userGroups.filter(userGroup => userGroup.type == 'restaurant')
        await localforage.setItem('restaurantUserGroups', JSON.stringify(this.restaurantUserGroups))
        await localforage.setItem('userGroups', JSON.stringify(this.userGroups))
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getSearchedUserGroups", error.message, null,null,null, error)
      }
    },

    async getNewUserGroups() {
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {

            // limit: 20
          }
        })

        // Merge the new data with the existing data
        this.userGroups = res.data

        //this might not be needed and/or inefficient
        await localforage.setItem('restaurantUserGroups', JSON.stringify(this.restaurantUserGroups))
        await localforage.setItem('userGroups', JSON.stringify(this.userGroups))
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getNewUserGroups", error.message, null,null,null, error)
      }
    },

    // Lightweight list fetch for all-groups admin page (no user/restaurant lookups)
    async getGroupsList(params = {}) {
      this.fetching = true
      try {
        let res = await axios.get(`${baseUrl}list-with-counts`, { params })
        this.userGroups = res.data
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:getGroupsList", error.message, null,null,null, error)
        this.fetching = false
      }
    },

    async searchGroupsList(searchTerm) {
      this.fetching = true
      try {
        let res = await axios.get(`${baseUrl}list-with-counts`, {
          params: { searchTerm }
        })
        this.userGroups = res.data
        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:searchGroupsList", error.message, null,null,null, error)
        this.fetching = false
      }
    },

    async getGuestUserGroup(publicId, groupType) {
      if(publicId == "reset"){
        localStorage.removeItem('homeRedirectPath')
        localStorage.removeItem('guestUserGroup')
        
        return window.location.href = "/home"
      }
      try {
        let res = await axios.get(`${baseUrl}${groupType}/${publicId}`, {
        })
        this.guestUserGroup = res.data
        // RM-223: `guestUserGroup` is the logged-OUT "resume joining" funnel. Only
        // persist it for a genuine guest (matches isUserLoggedIn() — userData AND
        // jwt). Belt-and-suspenders: if a logged-in member lands on a group page,
        // proactively clear any stale value so /home can never bounce them back to
        // /{group}/activate.
        const isUserLoggedIn = !!(localStorage.getItem('userData') && localStorage.getItem('jwt'))
        if (this?.guestUserGroup?.publicId) {
          if (isUserLoggedIn) {
            localStorage.removeItem('guestUserGroup')
          } else {
            localStorage.setItem('guestUserGroup', JSON.stringify(this.guestUserGroup))
          }
        }
      } catch (error) {
        if(error.response.status == 404){
          //if 'guestUserGroup' matches localStorage then clear localStorage and redirect to home page
          if(localStorage.getItem('guestUserGroup')){
            const guestUserGroup = JSON.parse(localStorage.getItem('guestUserGroup'))
            if(guestUserGroup.publicId == publicId){
              localStorage.removeItem('guestUserGroup')
              
              return window.location.href = "/home"
            }
            else{
              //redirect to 404 page
              window.location.href = "/404"
            }
          }
          else{
            //redirect to 404 page
            window.location.href = "/404"
          }

          //redirect to 404 page
          //window.location.href = "/404"
        }
        console.log("error", error)
        reportError("userGroups.store.js:getGuestUserGroup", error.message, null,null,null, error)
      }
    },

    async getPublicUserGroup(publicId, groupType) {
      try {
        let res = await axios.get(`${baseUrl}${groupType}/${publicId}`)
        
        return res.data
      } catch (error) {
        reportError("userGroups.store.js:getPublicUserGroup", error.message, null,null,null, error)
        
        return null
      }
    },

    async getPartnerGroups() {
      try {
        let res = await axios.get(`${baseUrl}partners`)
        
        return res.data
      } catch (error) {
        reportError("userGroups.store.js:getPartnerGroups", error.message, null,null,null, error)
        
        return []
      }
    },
    async requestPartnerConnection(groupId) {
      try {
        let res = await axios.post(`${baseUrl}request-partner-connection/${groupId}`, {}, {
          params:{
          }
        })
        
        return res.data
      } catch (error) {
        reportError("userGroups.store.js:requestPartnerConnection", error.message, null,null,null, error)
        throw error
      }
    },
    async getUserGroupById(groupId) {
      console.log("getUserGroupById", groupId)
      if(!this.fetching){
        try {
          let res = await axios.get(`${baseUrl}${groupId}`, {
            params:{
            }
          })

          if(res.data){
            let theGroup = this?.userGroups?.find(({ id }) => id === groupId)
            if(theGroup){
              Object.assign(theGroup, res.data)
              if(this.adminEditingUserGroup?.id == theGroup) Object.assign(this.adminEditingUserGroup, res.data)
              if(this.adminEditingUserGroupMembers?.id == theGroup) Object.assign(this.adminEditingUserGroupMembers, res.data)
            }
            else{
              this.userGroups.push(res.data)
            }

          }
        } catch (error) {
          console.log("Error dffhf3", error)
          reportError("reservations.store.js:getReservationById", error.message, null,null,null, error)
        }
      }
    },

    async adminEditUserGroup(idToEdit) {
      await this.getUserGroupById(idToEdit)

      // Create a shallow copy to prevent direct mutation of userGroups array
      // This avoids triggering filteredUserGroups re-computation on every form keystroke
      const original = this.userGroups.find(({ id }) => id === idToEdit)
      this.adminEditingUserGroup = original ? { ...original } : null
    },

    async adminEditUserGroupMembers(idToEdit) {
      await this.getUserGroupById(idToEdit)
      this.adminEditingUserGroupMembers = this.userGroups.find(({ id }) => id === idToEdit)
    },

    async cancelAdminEditUserGroup() {
      console.log("cancelAdminEditUserGroup", this.adminEditingUserGroup)
      if(this.adminEditingUserGroup.isNew){
        this.adminDeleteUserGroup(this.adminEditingUserGroup.id)
      } else{
        this.getUserGroupById(this.adminEditingUserGroup.id)
      }
      this.adminEditingUserGroup = null
    },

    async revertAdminEditUserGroupMembers() {
      this.getUserGroupById(this.adminEditingUserGroupMembers.id)
    },

    async cancelAdminEditUserGroupMembers() {
      this.getUserGroupById(this.adminEditingUserGroupMembers.id)
      this.adminEditingUserGroupMembers = null
    },

    async saveAdminEditingUserGroup(keepEditing) {
      try {
        let res = await axios.put(`${baseUrl}${this.adminEditingUserGroup.id}`, this.adminEditingUserGroup, {
          params:{
          }
        })
        this.adminEditingUserGroup.isNew = false
                
        const index = this.userGroups.findIndex(g => g.id === this.adminEditingUserGroup.id)
        if (index !== -1) {
          Object.assign(this.userGroups[index], res.data)
        }
                
        if(keepEditing){
          Object.assign(this.adminEditingUserGroup, res.data)
        } else{
          this.adminEditingUserGroup = null
        }
        await localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.error('Error saving user group:', error)
        console.error('Error response:', error.response?.data)

        // reportError("userGroups.store.js:saveAdminEditingUserGroup", error.message, null,null,null, error)
        return error.response?.data
      }

    },
    async saveAdminEditingUserGroupMembers() {

      let toSend = Object.assign({}, this.adminEditingUserGroupMembers)

      //remove all properties from toSend.userPermissions except for email and permission and uuid
      toSend.userPermissions = toSend.userPermissions.map(({email, permission, uuid, dateInvited, isNew, sendInvite, invitedBy, approve, receivesAllMemberApprovalsAndRequests, receivesAdministrativeCorrespondence, receivesGeneralRequests, receivesPrivateRequests, contactPreference}) => ({email, permission,uuid, dateInvited, isNew, sendInvite, invitedBy, approve, receivesAllMemberApprovalsAndRequests, receivesAdministrativeCorrespondence, receivesGeneralRequests, receivesPrivateRequests, contactPreference}))

      try {
        let res = await axios.put(`${baseUrl}${this.adminEditingUserGroupMembers.id}`, toSend, {
          params:{
          }
        })

        //the put call doesn't return extra data with userPermissions, so we don't want to overwrite it
        this.adminEditingUserGroupMembers = Object.assign({}, res.data)
        var updateIndex = this.userGroups.map(item => item.id).indexOf(this.adminEditingUserGroupMembers.id)
        this.userGroups[updateIndex] = this.adminEditingUserGroupMembers
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))

      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:saveAdminEditingUserGroupMembers", error.message, null,null,null, error)
      }

    },

    async adminDeleteUserGroup(idToDelete) {

      try {
        let res = await axios.delete(`${baseUrl}${idToDelete}`, {
          params:{
          }
        })

        //remove matching item from array
        var removeIndex = this.userGroups.map(item => item.id).indexOf(idToDelete)
        ~removeIndex && this.userGroups.splice(removeIndex, 1)

        if(this.adminEditingUserGroup?.id == idToDelete)
          this.adminEditingUserGroup = null


        console.log("Setting userGroups localStorage to ", this.userGroups)
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:adminDeleteUserGroup", error.message, null,null,null, error)
      }

    },

    async adminCloneUserGroup(sourceGroupId) {
      try {
        // Fetch the full group to get all fields (list endpoint only returns a subset)
        const fullRes = await axios.get(`${baseUrl}${sourceGroupId}`, {
          params: {}
        })
        const source = fullRes.data

        const cloneData = {
          name: `Copy of ${source.name || ''}`,
          type: source.type || 'discount',
          publicId: source.publicId ? `copy-of-${source.publicId}`.toLowerCase() : null,
          membershipPriceIndividualAnnual: source.membershipPriceIndividualAnnual,
          joinMessage: source.joinMessage,
          joinMessage2: source.joinMessage2,
          personalizedWelcomeMessage: source.personalizedWelcomeMessage,
          planIdToGive: source.planIdToGive,
          groupIdentity: source.groupIdentity,
          membershipMessage: source.membershipMessage,
        }

        const res = await axios.post(`${baseUrl}`, cloneData, {
          params: {}
        })
        this.adminEditingUserGroup = res.data
        this.adminEditingUserGroup.isNew = true
        this.userGroups.push(this.adminEditingUserGroup)
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:adminCloneUserGroup", error.message, null, null, null, error)
      }
    },

    async adminNewUserGroup() {
      //this.myReservation = { loading: true };
      try {
        let res = await axios.post(`${baseUrl}`, {}, {
          params:{
          }
        })
        this.adminEditingUserGroup = res.data
        this.adminEditingUserGroup.isNew = true
        this.userGroups.push(this.adminEditingUserGroup)
        console.log("Setting userGroups localforage to ", this.userGroups)
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:adminNewUserGroup", error.message, null,null,null, error)
      }
    },

    async adminNewPlatinumUserGroupForUser(user) {
      //this.myReservation = { loading: true };
      try {
        let res = await axios.post(`${baseUrl}`, {
          "name": `${user.firstName} ${user.lastName}`,
          type: "enterprise",
          "membershipMessage": `Provided by ${user.firstName} ${user.lastName}`,
          "numberOfSeats": 1,
          "pricePerSeatPerBillingPeriod": 150,
          "userPermissions": [{permission: "owner", email: user.email}],
          subscriptions: []
        }, {
          params:{
          }
        })
        this.adminEditingUserGroupSubscription = res.data
        this.adminEditingUserGroupSubscription.isNew = true
        this.userGroups.push(this.adminEditingUserGroupSubscription)
        console.log("Setting userGroups localforage to ", this.userGroups)
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.error(error)
        reportError("userGroups.store.js:adminNewUserGroup", error.message, null,null,null, error)
      }
    },

    async adminEditUserGroupSubscription(idToEdit) {
      //fetch the full user so we have all the subscription info
      //await this.fetchUserById(idToEdit)
      //make a copy of the user to edit
      await this.getUserGroupById(idToEdit)
      this.adminEditingUserGroupSubscription = Object.assign({}, this.userGroups.find(({ id }) => id === idToEdit))
      console.log("adminEditingUserGroupSubscription", this.adminEditingUserGroupSubscription)
    },
    async cancelAdminEditUserGroupSubscription() {
      console.log("Canceling", this.adminEditingUserGroupSubscription)
      this.adminEditingUserGroupSubscription = null
    },
    async saveAdminEditingUserGroupSubscription() {
      console.log("Saving")
      try {
        let res = await axios.put(`${baseUrl}${this.adminEditingUserGroupSubscription.id}`, this.adminEditingUserGroupSubscription, {
          params:{
          }
        })
        this.adminEditingUserGroupSubscription = Object.assign({}, res.data)
        var updateIndex = this.userGroups.map(item => item.id).indexOf(this.adminEditingUserGroupSubscription.id)
        this.userGroups[updateIndex] = this.adminEditingUserGroupSubscription
        localforage.setItem('userGroups', JSON.stringify(this.userGroups))
      } catch (error) {
        console.log("error", error)
        reportError("userGroups.store.js:saveAdminEditingUserGroupSubscription", error.message, null,null,null, error)
      }

    }



  }
})
